// Lucide path data (bug, file-text, headphones, sparkles,
// sliders-horizontal), inlined because the snippet ships without React or the
// icon package. Shared by the reply preview and the intro card. Keep in sync
// with the UI package's `ClassificationIcon` and
// `WidgetOnboardingToneGlyph`.
const GLYPH_NODES: Readonly<
	Record<
		string,
		readonly (readonly [string, Readonly<Record<string, string>>])[]
	>
> = {
	bug: [
		["path", { d: "M12 20v-9" }],
		["path", { d: "M14 7a4 4 0 0 1 4 4v3a6 6 0 0 1-12 0v-3a4 4 0 0 1 4-4z" }],
		["path", { d: "M14.12 3.88 16 2" }],
		["path", { d: "M21 21a4 4 0 0 0-3.81-4" }],
		["path", { d: "M21 5a4 4 0 0 1-3.55 3.97" }],
		["path", { d: "M22 13h-4" }],
		["path", { d: "M3 21a4 4 0 0 1 3.81-4" }],
		["path", { d: "M3 5a4 4 0 0 0 3.55 3.97" }],
		["path", { d: "M6 13H2" }],
		["path", { d: "m8 2 1.88 1.88" }],
		["path", { d: "M9 7.13V6a3 3 0 1 1 6 0v1.13" }],
	],
	business: [
		[
			"path",
			{
				d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z",
			},
		],
		["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
		["path", { d: "M10 9H8" }],
		["path", { d: "M16 13H8" }],
		["path", { d: "M16 17H8" }],
	],
	feature: [
		[
			"path",
			{
				d: "M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z",
			},
		],
		["path", { d: "M20 2v4" }],
		["path", { d: "M22 4h-4" }],
		["circle", { cx: "4", cy: "20", r: "2" }],
	],
	it: [
		["path", { d: "M10 5H3" }],
		["path", { d: "M12 19H3" }],
		["path", { d: "M14 3v4" }],
		["path", { d: "M16 17v4" }],
		["path", { d: "M21 12h-9" }],
		["path", { d: "M21 19h-5" }],
		["path", { d: "M21 5h-7" }],
		["path", { d: "M8 10v4" }],
		["path", { d: "M8 12H3" }],
	],
	support: [
		[
			"path",
			{
				d: "M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3",
			},
		],
	],
};

const SVG_NS = "http://www.w3.org/2000/svg";

/** The classification glyph as an inline SVG, empty for an unknown kind. */
export const glyphOf = (kind: string): SVGSVGElement => {
	const svg = document.createElementNS(SVG_NS, "svg");
	svg.setAttribute("viewBox", "0 0 24 24");
	svg.setAttribute("fill", "none");
	svg.setAttribute("stroke", "currentColor");
	svg.setAttribute("stroke-width", "2");
	svg.setAttribute("stroke-linecap", "round");
	svg.setAttribute("stroke-linejoin", "round");
	svg.setAttribute("aria-hidden", "true");
	for (const [tag, attrs] of GLYPH_NODES[kind] ?? []) {
		const node = document.createElementNS(SVG_NS, tag);
		for (const [name, value] of Object.entries(attrs)) {
			node.setAttribute(name, value);
		}
		svg.append(node);
	}
	return svg;
};

/**
 * A bundled SVG string built as DOM nodes. `innerHTML` is a Trusted Types
 * sink: on a host page that enforces them it throws and takes the launcher
 * down with it. Reads only the markup this bundle ships – elements and
 * double-quoted attributes, no text or entities.
 */
export const svgFrom = (markup: string): DocumentFragment => {
	const fragment = document.createDocumentFragment();
	const open: ParentNode[] = [fragment];
	for (const [
		,
		closing,
		tag = "",
		attributes = "",
		selfClosing,
	] of markup.matchAll(/<(\/?)([\w-]+)([^>]*?)(\/?)>/gu)) {
		if (closing === "/") {
			open.pop();
			continue;
		}
		const node = document.createElementNS(SVG_NS, tag);
		for (const [, name = "", value = ""] of attributes.matchAll(
			/([\w:-]+)="([^"]*)"/gu,
		)) {
			node.setAttribute(name, value);
		}
		open.at(-1)?.append(node);
		if (selfClosing !== "/") {
			open.push(node);
		}
	}
	return fragment;
};
